import React, { useContext, useEffect, useState, useRef } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Avatar,
  Stack,
  CircularProgress,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  LinearProgress,
  // ToggleButton, // 未使用のためコメントアウト
  // ToggleButtonGroup, // 未使用のためコメントアウト
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import DiamondIcon from '@mui/icons-material/Diamond';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import { LoginInfo } from '../App';
import ApiFetch from './ApiFetch';
import { useTranslation } from "react-i18next";
import ReactMarkdown from 'react-markdown';
import SearchLoadingText from './common/SearchLoadingText';
import { Link } from 'react-router-dom';

const API_BASE_URL = ApiFetch.API_PROTOCOL + "//" + ApiFetch.API_HOST;

const DEFAULT_IMAGE_PARAMS = {
  mode: "txt2img",
  paymentMethod: "point",
  random: 0,
  count: "1",
  style: "waiNSFWIllustrious_v120",
  isFullMode: false,
  isFastMode: false,
  isScaleUpx2: false,
  enterJpPrompt: false,
  enterEnhancePrompt: false,
  sampler: "Euler a",
  upscaler: "R-ESRGAN 4x+ Anime6B",
  steps: "20",
  scale: "4",
  denoisingStrength: "0.35",
  seed: "-1",
  width: "896",
  height: "1152"
};

// 候補のキー (国際化用)
const aiAssistantSuggestionKeys = [
  "aitalk.suggestions.ai.weather",
  "aitalk.suggestions.ai.joke",
  "aitalk.suggestions.ai.news",
  "aitalk.suggestions.ai.recipe",
];

const characterSuggestionKeys = [
  "aitalk.suggestions.character.aboutYou",
  "aitalk.suggestions.character.skills",
  "aitalk.suggestions.character.recentTopic",
  "aitalk.suggestions.character.hobby",
];

// 候補表示コンポーネント
const SuggestionsDisplay = ({ suggestions, onSuggestionClick, titleKey }) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ textAlign: 'center', p: 2, width: '100%', maxWidth: 'md', mt: 'auto', mb: 'auto' }}>
      <Typography variant="subtitle1" gutterBottom sx={{ color: 'text.secondary', mb: 3 }}>
        {t(titleKey)}
      </Typography>
      <Stack
        direction="row"
        spacing={1.5}
        justifyContent="center"
        flexWrap="wrap"
        gap={1.5}
      >
        {suggestions.map((suggestionKey, index) => (
          <Button
            key={index}
            variant="outlined"
            size="medium"
            onClick={() => onSuggestionClick(t(suggestionKey))}
            sx={{
              textTransform: 'none',
              borderRadius: '16px',
              borderColor: 'grey.400',
              color: 'text.primary',
              padding: '6px 16px',
              '&:hover': {
                backgroundColor: 'action.hover',
                borderColor: 'grey.500',
              }
            }}
          >
            {t(suggestionKey)}
          </Button>
        ))}
      </Stack>
    </Box>
  );
};


const AITalk = ({ show, setPoint, characterId = "", characterName = "AIアシスタント", setAIAssistant, hiddenCloseButton = false }) => {
  const { t } = useTranslation();
  const { loginInfo } = useContext(LoginInfo);
  const [comments, setComments] = useState([]);
  const [talking, setTalking] = useState(false);
  const [cost, setCost] = useState(0);
  const [message, setMessage] = useState("");
  const [inputText, setInputText] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [latestResponse, setLatestResponse] = useState(null); // 未使用のためコメントアウト候補 (generateImageで使われているか確認) -> 使われていたので戻す

  // Image generation states
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [currentProcId, setCurrentProcId] = useState(null);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [currentContent, setCurrentContent] = useState(null);
  const [imageProgress, setImageProgress] = useState(0);
  const [callGenerateImage, setCallGenerateImage] = useState(false)

  const isMounted = useRef(true);
  const messagesContainerRef = useRef(null);
  const shouldAutoScroll = useRef(true);

  // 初期メッセージのローディング状態
  const [loadingInitialMessages, setLoadingInitialMessages] = useState(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      shouldAutoScroll.current = scrollHeight - (scrollTop + clientHeight) < 100;
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!shouldAutoScroll.current) return;
    const scrollToBottom = () => {
      const container = messagesContainerRef.current;
      if (container && isMounted.current) {
        container.scrollTop = container.scrollHeight;
      }
    };
    scrollToBottom();
    const images = messagesContainerRef.current?.getElementsByTagName('img') || [];
    const imagePromises = Array.from(images).map(img => {
      if (img.complete) return Promise.resolve();
      return new Promise(resolve => {
        img.onload = resolve;
        img.onerror = resolve;
      });
    });
    Promise.all(imagePromises).then(() => {
      if (isMounted.current) {
        scrollToBottom();
      }
    });
  }, [comments, show]);

  const profilePath = characterId === undefined || characterId === "" // characterIdが空文字列の場合も考慮
    ? "/img/logo_s.png"
    : `${ApiFetch.API_PROTOCOL}//${ApiFetch.IMG_HOST}/character/post/t/${characterId}_in.webp?`;

  const getLatest = async (characterId) => {
    const response = await new ApiFetch().get(
      `${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/chat/question/${characterId}`
    );
    return response.json();
  };

  const talk = async (content, characterId, generateImage) => {
    const response = await new ApiFetch().post(
      `${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/chat/question`,
      { content, characterId, generateImage }
    );
    return response.json();
  };

  const searchApi = async (searchId) => {
    const response = await new ApiFetch().post(
      `${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/chat/search`,
      { searchId, searchId } // searchIdを2回渡しているのは意図的か確認 -> API仕様による
    );
    return response.json();
  };

  const generateImage = async (params) => {
    try {
      const response = await new ApiFetch().post(`${API_BASE_URL}/v1/sd/txt2img`, params);
      if (!response) throw new Error(t('aitalk.error.imageGenerationRequestFailed'));
      const data = await response.json();
      if (!data.result) throw new Error(data.errors || t('aitalk.error.imageGenerationStartFailed'));
      return data.data.procId;
    } catch (err) {
      throw new Error(`${t('aitalk.error.imageGenerationErrorPrefix')}: ${err.message}`);
    }
  };

  const checkImageStatus = async (procId) => {
    try {
      const response = await new ApiFetch().get(`${API_BASE_URL}/v1/sd/status/${procId}`);
      if (!response) throw new Error(t('aitalk.error.statusCheckFailed'));
      const data = await response.json();
      return {
        status: data.data.status,
        progress: data.data.progress
      };
    } catch (err) {
      throw new Error(`${t('aitalk.error.statusCheckErrorPrefix')}: ${err.message}`);
    }
  };

  useEffect(() => {
    const processImages = async () => {
      if (comments.some(c => c.isImage && c.imageUrl)) return; // imageUrlもチェック
      const processedComments = await Promise.all(
        comments.map(async (item) => {
          if (!item.proc_id || item.isImage) return item;
          try {
            let parsedContent;
            try {
              const match = item.content.match(/```(?:json)?\s*\n?([\s\S]*?)\n?```/);
              const jsonStr = match ? match[1] : item.content;
              parsedContent = JSON.parse(jsonStr);
            } catch (parseError) {
              // JSONパースエラーでも画像処理を試みる場合があるので、ここではreturn itemしない
            }
            const response = await new ApiFetch().get(
              `${API_BASE_URL}/v1/sd/getImageSource/${item.proc_id}/1/0`
            );
            if (!response) {
              return { ...item, content: t('aitalk.image.loadError'), isImage: false }; // 画像読み込み失敗メッセージ
            }
            const blob = await response.blob();
            const imageUrl = URL.createObjectURL(blob);
            return {
              ...item,
              // content: parsedContent ? parsedContent.prompt || t('aitalk.image.generatedImage') : t('aitalk.image.generatedImage'), // promptがなければ汎用テキスト
              imageUrl: imageUrl,
              isImage: true
            };
          } catch (error) {
            console.error('Error processing item:', error);
            return { ...item, content: t('aitalk.image.processingError'), isImage: false };
          }
        })
      );
      if (JSON.stringify(processedComments) !== JSON.stringify(comments)) {
        setComments(processedComments);
      }
    };
    if (comments.length > 0) { // commentsが空でない場合のみ実行
        processImages();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comments, t]); // t を依存配列に追加

  const getGeneratedImage = async (procId) => {
    try {
      const response = await new ApiFetch().get(`${API_BASE_URL}/v1/sd/getImageSource/${procId}/1/0`);
      if (!response) throw new Error(t('aitalk.error.getGeneratedImageFailed'));
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (err) {
      throw new Error(`${t('aitalk.error.getImageErrorPrefix')}: ${err.message}`);
    }
  };

  useEffect(() => {
    let intervalId;
    const monitorProgress = async () => {
      if (!currentProcId) return;
      try {
        const statusData = await checkImageStatus(currentProcId);
        if (isMounted.current) setImageProgress(statusData.progress);

        if (statusData.status === 3) {
          const imageUrl = await getGeneratedImage(currentProcId);
          if (isMounted.current) {
            setComments(prev => {
              const filteredComments = prev.filter(comment =>
                !(comment.role === "assistant" && comment.content === t('aitalk.image.generatingPlaceholder'))
              );
              return [...filteredComments, {
                content: currentContent || t('aitalk.image.generatedImage'), // currentContent がない場合のフォールバック
                role: "assistant",
                imageUrl: imageUrl,
                isImage: true
              }];
            });
            setIsGeneratingImage(false);
            setCurrentProcId(null);
            setCurrentChatId(null);
            setCurrentContent(null);
            setImageProgress(0);
          }
          clearInterval(intervalId);
        }
        if (statusData.status === 4) {
          throw new Error(t('aitalk.error.imageGenerationFailedStatus'));
        }
        if (statusData.status === 6 || statusData.status === 7) {
          throw new Error(t('aitalk.error.imageGenerationBlocked'));
        }
      } catch (err) {
        console.error('Error in monitorProgress:', err);
        if (isMounted.current) {
          setComments(prev =>
            prev.filter(comment =>
              !(comment.role === "assistant" && comment.content === t('aitalk.image.generatingPlaceholder'))
            )
          );
          setMessage(err.message);
          setIsGeneratingImage(false);
          setCurrentProcId(null);
          setCurrentChatId(null);
          setCurrentContent(null);
          setImageProgress(0);
        }
        clearInterval(intervalId);
      }
    };

    if (currentProcId) {
      monitorProgress();
      intervalId = setInterval(monitorProgress, 1000);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProcId, currentContent, t]); // t を依存配列に追加

  useEffect(() => {
    const fetchOriginalMessages = async () => {
      if (!characterId && characterName !== "AIアシスタント") { // characterId がなく、デフォルトのAIアシスタントでもない場合
        setLoadingInitialMessages(false);
        setComments([]); // 履歴は空で開始
        return;
      }
      setLoadingInitialMessages(true);
      try {
        const response = await getLatest(characterId);
        if (isMounted.current) {
          if (response.result) {
            setComments(response.data);
          } else {
            setComments([]);
          }
        }
      } catch (error) {
        console.error('Error fetching messages:', error);
        if (isMounted.current) {
          setComments([]);
        }
      } finally {
        if (isMounted.current) {
          setLoadingInitialMessages(false);
        }
      }
    };
    fetchOriginalMessages();
  }, [characterId, characterName]); // characterNameも依存配列に追加

  const calculateInputCost = (text) => Math.ceil(text.length);

  const handleSearch = async (searchId, newComments) => {
    setComments([...newComments, { role: "assistant", content: t('aitalk.searchingGoogle') }]);
    try {
      const response = await searchApi(searchId);
      if (isMounted.current) {
        if (response.result) {
          //setPoint(response.data.point); // APIからポイント情報が返る場合
          const aiResponse = response.data.content;
          setComments(prevComments => [
            ...prevComments.filter(comment =>
              !(comment.role === "assistant" && comment.content === t('aitalk.searchingGoogle'))
            ),
            { role: "assistant", content: response.data.search_result_summary, title: response.data.search_source.title, link: response.data.search_source.link },
            { role: "assistant", content: aiResponse },
          ]);
        } else {
          setMessage(response.errors || t('aitalk.error.generic'));
        }
      }
    } catch (error) {
      if (isMounted.current) setMessage(t('aitalk.error.network'));
    } finally {
      if (isMounted.current) setTalking(false);
    }
  };

  const handleSend = async (textOverride = null) => {
    const textToSend = textOverride !== null ? textOverride : inputText;

    if (!loginInfo.result) {
      setDialogOpen(true);
      return;
    }
    if (textToSend.trim().length === 0) {
      setMessage(t("aitalk.error.emptyMessage"));
      return;
    }
    if (textToSend.trim().length > 2500) { // 文字数制限チェック
      setMessage(t("aitalk.error.tooLongMessage", { maxLength: 2500 }));
      return;
    }

    setTalking(true);
    setMessage("");
    const newComments = [...comments, { role: "user", content: textToSend }];
    setComments(newComments);

    // ユーザーが候補をクリックした場合でも、送信後にテキストボックスはクリアする
    if (textOverride !== null) {
        setInputText(""); // 候補からの送信の場合、送信後すぐにクリア
        setCost(0);
    }


    try {
      const response = await talk(textToSend, characterId, callGenerateImage);
      if (isMounted.current) {
        if (response.result) {
          setPoint(response.data.point);
          // setCost(0); // 送信成功時のコスト表示は入力欄クリア時に連動させる

          const aiResponse = response.data.content;

          if (response.data.search_id) {
            handleSearch(response.data.search_id, newComments);
            // setInputText(""); // handleSearch内でクリアされるか、ここでクリア
            // setCost(0);
            return; // handleSearchが後処理するのでここで終了
          }

          if (response.data.proc_id && response.data.proc_id.length === 36) {
            setComments([...newComments, {
              role: "assistant",
              content: t('aitalk.image.generatingPlaceholder')
            }]);
            setIsGeneratingImage(true);
            const procId = response.data.proc_id;
            setCurrentProcId(procId);
            setCurrentChatId(response.data.chat_id);
            setCurrentContent(response.data.content); // AIの応答メッセージ（JSONである可能性も）
            setLatestResponse(response);
          } else {
            setComments([...newComments, {
              role: "assistant",
              content: aiResponse
            }]);
          }
          setInputText(""); // 通常送信または画像生成開始後にクリア
          setCost(0);
        } else {
          setMessage(response.errors || t('aitalk.error.generic'));
          // エラー時はユーザーの入力は残すため、newCommentsから元に戻す
          setComments(prev => prev.slice(0, -1));
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
      if (isMounted.current) {
        setMessage(t('aitalk.error.network'));
        setComments(prev => prev.slice(0, -1)); // ネットワークエラー時もユーザーの入力を残す
      }
    } finally {
      if (isMounted.current) {
        setTalking(false);
      }
    }
  };

  const handleSuggestionClick = (suggestionText) => {
    if (!loginInfo.result) {
      setDialogOpen(true);
      return;
    }
    setInputText(suggestionText); // 入力欄に表示（ユーザーに確認させるため）
    setCost(calculateInputCost(suggestionText)); // コスト表示も更新
    // 即時送信する場合は下の行をアンコメント
    handleSend(suggestionText);
  };


  if (!show) return null;

  const currentSuggestions = characterName === "AIアシスタント" ? aiAssistantSuggestionKeys : characterSuggestionKeys;
  const suggestionTitleKey = characterName === "AIアシスタント" ? "aitalk.suggestions.ai.title" : "aitalk.suggestions.character.title";

  return (
    <Container maxWidth="md" sx={{ p: 0 }}>
      <Box sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Paper
          elevation={3}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#fafafa',
            overflow: 'hidden'
          }}
        >
          <Box
            ref={messagesContainerRef}
            id="ai-talk-messages"
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              ...(comments.length === 0 && !talking && !isGeneratingImage && !loadingInitialMessages && {
                justifyContent: 'center',
                alignItems: 'center',
              })
            }}
          >
            {loadingInitialMessages && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
              </Box>
            )}
            {!loadingInitialMessages && comments.length === 0 && !talking && !isGeneratingImage && (
              <SuggestionsDisplay
                suggestions={currentSuggestions}
                onSuggestionClick={handleSuggestionClick}
                titleKey={suggestionTitleKey}
              />
            )}
            {comments.map((comment, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: comment.role === 'assistant' ? 'flex-start' : 'flex-end',
                }}
              >
                <Paper
                  elevation={1}
                  sx={{
                    maxWidth: comment.isImage ? '90%' : '70%',
                    p: comment.isImage ? 1 : 2, // 画像の場合はパディングを小さく
                    backgroundColor: comment.role === 'assistant' ? '#ffffff' : '#e3f2fd',
                    borderRadius: 2,
                    overflow: 'hidden', // 画像が角丸からはみ出ないように
                  }}
                >
                  {comment.role === 'assistant' && (
                    <Stack direction="row" spacing={1} alignItems="center" mb={comment.isImage ? 0.5 : 1}>
                      <Avatar
                        src={profilePath}
                        alt={characterName}
                        sx={{ width: 32, height: 32 }}
                      />
                      <Typography variant="subtitle2">{characterName}</Typography>
                    </Stack>
                  )}
                  {comment.role === 'assistant' && comment.content === t('aitalk.searchingGoogle') ? <SearchLoadingText /> : (
                    <ReactMarkdown>{comment.content}</ReactMarkdown>
                  )}
                  {comment.title && comment.title.length > 0 && (
                     <Typography variant="caption" display="block" sx={{mt: 1, color: 'text.secondary'}}>
                       {t('aitalk.source')}: {comment.title} - <a href={comment.link} target="_blank" rel="noopener noreferrer">{comment.link}</a>
                     </Typography>
                  )}
                  {comment.isImage && comment.imageUrl && (
                    <img
                      src={comment.imageUrl}
                      alt={t('aitalk.image.generatedImageAlt')}
                      style={{
                        display: 'block', // blockにすることで親要素のパディングが適用されやすくなる
                        width: '100%', // 親要素の幅に合わせる
                        maxWidth: '100%', // 最大幅も100%
                        height: 'auto', // アスペクト比を維持
                        borderRadius: '4px', // 画像自体にも少し角丸
                        marginTop: comment.content && comment.content !== t('aitalk.image.generatedImage') ? '8px' : '0' // テキストがある場合はマージン
                      }}
                    />
                  )}
                </Paper>
              </Box>
            ))}

            {(talking || (isGeneratingImage && !currentProcId)) && ( // procIdがない画像生成中は「考え中」に近い
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2, mt:1 }}>
                <Avatar src={profilePath} sx={{ width: 32, height: 32 }} />
                <Typography>{t('aitalk.thinking')}</Typography>
                <CircularProgress size={20} />
              </Box>
            )}
             {/* 画像生成中（プログレス表示前）の専用ローディング */}
            {isGeneratingImage && currentProcId && imageProgress === 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2, mt:1 }}>
                  <Avatar src={profilePath} sx={{ width: 32, height: 32 }} />
                  <Typography>{t('aitalk.image.generatingInProgress')}</Typography>
                  <CircularProgress size={20} />
              </Box>
            )}
          </Box>

          {isGeneratingImage && imageProgress > 0 && (
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{mb: 0.5}}>
                {t('aitalk.image.generatingProgressTitle')}
              </Typography>
              <LinearProgress variant="determinate" value={imageProgress} />
              <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{mt: 0.5}}>
                {imageProgress}% {t('common.complete')}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              p: 1.5,
              backgroundColor: '#ffffff',
              borderTop: 1,
              borderColor: 'divider'
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {characterName === "AIアシスタント" && loginInfo.result && ( // ログイン時のみ表示
                <Typography variant="caption" sx={{ textAlign: 'center' }}>
                  <Link to={"/aitalk/46358de5-3c2c-4f14-8234-7b665d8bf345"}>{t('aitalk.newCharacterLink')}</Link>
                </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative' }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={1}
                  maxRows={4}
                  size="small"
                  value={inputText}
                  onChange={(e) => {
                    const newText = e.target.value;
                    setInputText(newText);
                    setCost(calculateInputCost(newText));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !talking && !isGeneratingImage) {
                        e.preventDefault(); // 改行を防ぐ
                        handleSend();
                    }
                  }}
                  placeholder={t('aitalk.inputPlaceholder')}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={callGenerateImage ? t('aitalk.imageGenerationOnTooltip') : t('aitalk.imageGenerationOffTooltip')}>
                          <IconButton
                            edge="end"
                            size="small"
                            disabled={talking || isGeneratingImage}
                            onClick={() => setCallGenerateImage(!callGenerateImage)}
                            sx={{
                              color: callGenerateImage ? 'primary.main' : 'action.active',
                              backgroundColor: callGenerateImage ? 'primary.lighter' : 'transparent', // MUI v5 の theme を想定
                              borderRadius: '50%',
                              p: 1,
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                backgroundColor: callGenerateImage
                                  ? 'primary.light'
                                  : 'action.hover',
                              },
                              position: 'relative',
                              '&::after': callGenerateImage ? {
                                content: '""',
                                position: 'absolute',
                                right: 2,
                                top: 2,
                                width: 8,
                                height: 8,
                                backgroundColor: 'success.main',
                                borderRadius: '50%',
                                border: '1px solid white',
                              } : {}
                            }}
                          >
                            <ImageSearchIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#ffffff' // 通常は theme.palette.background.paper
                      }
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={() => handleSend()} // 引数なしで呼び出し
                  disabled={talking || isGeneratingImage || inputText.trim().length === 0} // 入力空でも無効化
                  sx={{
                    height: '40px', // TextField small size (InputBase) の高さに合わせる
                    minWidth: 'auto', // IconButtonのように最小幅を自動に
                    px: 1.5 // パディング調整
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <SendIcon fontSize="small" />
                    {cost > 0 && ( // コストが0より大きい場合のみポイントアイコンと数値を表示
                        <>
                            <DiamondIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                            <Typography variant="body2" sx={{ lineHeight: 1 }}>{cost}</Typography>
                        </>
                    )}
                  </Stack>
                </Button>
                {!hiddenCloseButton && (
                  <Button
                    variant="outlined"
                    onClick={() => setAIAssistant(false)}
                    sx={{
                      height: '40px',
                      minWidth: 'auto',
                      px: 1
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </Button>
                )}
              </Box>
              {callGenerateImage && !talking && !isGeneratingImage && (
                <Typography
                  variant="caption"
                  color="primary.main"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 0.5, mt: -0.5 }}
                >
                  <Box component="span" sx={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', backgroundColor: 'success.main' }} />
                  {t('aitalk.imageGenerationMode')}
                </Typography>
              )}
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ mt: message ? 0.5 : 0, minHeight: '20px' }} // メッセージ表示エリアの高さを確保
              >
                {message && (
                  <Typography color="error" variant="body2">
                    {message}
                  </Typography>
                )}
              </Stack>
            </Box>
          </Box>
        </Paper>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      >
        <DialogTitle>{t("common.loginToContinue")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("aitalk.loginRequiredMessage")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            component={Link} // Linkコンポーネントとして動作
            to="/login"      // ログインページへのパス
            onClick={() => setDialogOpen(false)}
          >
            {t("login.login")}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AITalk;